import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import { useConfirm } from '../../components/ui/ConfirmDialog';
import {
  userService,
  User,
  CreateUserData,
  UpdateUserData,
} from '../../services/user.service';
import { LoadingState } from '../../components/ui/LoadingState';
import { ErrorState } from '../../components/ui/ErrorState';
import { EmptyState } from '../../components/ui/EmptyState';
import { StatusBadge } from '../../components/ui/StatusBadge';

const ROLES = [
  { value: 'sys_admin', label: 'Administrador del Sistema' },
  { value: 'general_manager', label: 'Gerente General' },
  { value: 'project_lead', label: 'Líder de Proyecto' },
  { value: 'accountant', label: 'Contador' },
  { value: 'contractor', label: 'Contratista' },
  { value: 'receptionist', label: 'Recepcionista' },
  { value: 'data_entry', label: 'Capturista de Datos' },
  { value: 'client', label: 'Cliente' },
];

const ROLE_LABEL: Record<string, string> = ROLES.reduce(
  (acc, r) => ({ ...acc, [r.value]: r.label }),
  {}
);

interface UserForm {
  email: string;
  name: string;
  password: string;
  role: string;
  phone: string;
  isActive: boolean;
}

const EMPTY_FORM: UserForm = {
  email: '',
  name: '',
  password: '',
  role: 'data_entry',
  phone: '',
  isActive: true,
};

export function UsersPage() {
  const { user: currentUser } = useAuth();
  const { showSuccess, showError } = useToast();
  const confirm = useConfirm();

  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<User | null>(null);
  const [form, setForm] = useState<UserForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    setError(null);
    userService
      .getUsers()
      .then(setUsers)
      .catch((e) => setError(e.message || 'Error al cargar'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return users.filter((u) => {
      if (filterRole !== 'all' && u.role !== filterRole) return false;
      if (!term) return true;
      return (
        u.name.toLowerCase().includes(term) ||
        u.email.toLowerCase().includes(term)
      );
    });
  }, [users, search, filterRole]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (u: User) => {
    setEditing(u);
    setForm({
      email: u.email,
      name: u.name,
      password: '',
      role: u.role,
      phone: u.phone ?? '',
      isActive: u.isActive,
    });
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.role) {
      showError('Completa nombre, email y rol');
      return;
    }
    if (!editing && form.password.length < 8) {
      showError('La contraseña debe tener al menos 8 caracteres');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const update: UpdateUserData = {
          name: form.name,
          role: form.role,
          phone: form.phone || undefined,
          isActive: form.isActive,
        };
        await userService.updateUser(editing.id, update);
        showSuccess('Usuario actualizado');
      } else {
        const create: CreateUserData = {
          email: form.email.trim(),
          name: form.name,
          password: form.password,
          role: form.role,
          phone: form.phone || undefined,
        };
        await userService.createUser(create);
        showSuccess('Usuario creado');
      }
      setShowForm(false);
      setEditing(null);
      setForm(EMPTY_FORM);
      load();
    } catch (err: any) {
      showError(err.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (u: User) => {
    if (u.id === currentUser?.id) {
      showError('No puedes desactivar tu propio usuario');
      return;
    }
    if (
      !(await confirm({
        message: `¿${u.isActive ? 'Desactivar' : 'Activar'} a ${u.name}?`,
        danger: u.isActive,
      }))
    )
      return;
    try {
      await userService.toggleUserStatus(u.id, !u.isActive);
      showSuccess('Estado actualizado');
      load();
    } catch (e: any) {
      showError(e.message || 'Error');
    }
  };

  return (
    <div className="space-y-4 max-w-lg mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Usuarios</h1>
          <p className="text-xs text-slate-500">{users.length} registrados</p>
        </div>
        <button
          onClick={openCreate}
          className="px-3 py-2 rounded-lg bg-primary text-white text-sm font-semibold"
        >
          + Nuevo
        </button>
      </div>

      <div className="relative">
        <input
          type="search"
          placeholder="Buscar por nombre o email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-white border border-slate-200 text-sm focus:border-primary focus:outline-none"
        />
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">🔍</span>
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-4 px-4">
        <button
          onClick={() => setFilterRole('all')}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap ${
            filterRole === 'all'
              ? 'bg-primary text-white'
              : 'bg-white text-slate-600 border border-slate-200'
          }`}
        >
          Todos
        </button>
        {ROLES.map((r) => (
          <button
            key={r.value}
            onClick={() => setFilterRole(r.value)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap ${
              filterRole === r.value
                ? 'bg-primary text-white'
                : 'bg-white text-slate-600 border border-slate-200'
            }`}
          >
            {r.label.split(' ')[0]}
          </button>
        ))}
      </div>

      {loading ? (
        <LoadingState />
      ) : error ? (
        <ErrorState message={error} onRetry={load} />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon="👥"
          title={users.length === 0 ? 'Sin usuarios' : 'Sin resultados'}
          description={
            users.length === 0
              ? 'Crea el primer usuario del sistema.'
              : 'Prueba con otro término o filtro.'
          }
        />
      ) : (
        <ul className="space-y-2">
          {filtered.map((u) => (
            <li key={u.id} className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center flex-shrink-0">
                  {u.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="text-sm font-semibold text-slate-900 truncate">
                      {u.name}
                    </p>
                    <StatusBadge status={u.isActive ? 'active' : 'inactive'} />
                  </div>
                  <p className="text-xs text-slate-500 truncate">{u.email}</p>
                  <p className="text-[11px] text-slate-400 truncate">
                    {ROLE_LABEL[u.role] ?? u.role}
                  </p>
                </div>
              </div>
              <div className="flex gap-1.5 mt-2 pt-2 border-t border-slate-100">
                <button
                  onClick={() => openEdit(u)}
                  className="flex-1 py-1.5 rounded-md bg-slate-100 text-slate-700 text-xs font-semibold"
                >
                  Editar
                </button>
                <button
                  onClick={() => handleToggle(u)}
                  disabled={u.id === currentUser?.id}
                  className={`flex-1 py-1.5 rounded-md text-xs font-semibold disabled:opacity-50 ${
                    u.isActive
                      ? 'bg-red-50 text-red-600'
                      : 'bg-emerald-50 text-emerald-600'
                  }`}
                >
                  {u.isActive ? 'Desactivar' : 'Activar'}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center">
          <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">
                {editing ? 'Editar usuario' : 'Nuevo usuario'}
              </h2>
              <button
                onClick={() => setShowForm(false)}
                className="text-slate-400 text-2xl leading-none"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Nombre *
                </label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  disabled={!!editing}
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none disabled:bg-slate-50"
                />
                {editing && (
                  <p className="text-[11px] text-slate-400 mt-1">
                    El email no se puede modificar
                  </p>
                )}
              </div>
              {!editing && (
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Contraseña * (mín. 8 caracteres)
                  </label>
                  <input
                    type="password"
                    required
                    minLength={8}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
                  />
                </div>
              )}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Rol *
                </label>
                <select
                  required
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
                >
                  {ROLES.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Teléfono
                </label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
                />
              </div>
              {editing && (
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                    className="w-4 h-4 accent-primary"
                  />
                  Usuario activo
                </label>
              )}
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="flex-1 py-2.5 rounded-lg bg-slate-100 text-slate-700 font-semibold text-sm"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 py-2.5 rounded-lg bg-primary text-white font-semibold text-sm disabled:opacity-50"
                >
                  {saving ? 'Guardando…' : editing ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}